/**
 * Roof Claim agreement boxes that repeat what was typed when creating the
 * customer (Jaron, Oct 30 + Nov 1, 13433630443: "These need to match what we
 * write down when creating the customer so we are doing double work").
 *
 * Same contract as the authorization form (V99):
 * - unsigned: a typed value wins, otherwise the customer's data is SHOWN.
 *   Nothing is written on open. `roof_claiming_email` is roof-claim evidence
 *   (V92), so writing it on view would put the agreement on other deals;
 * - first roof signature: what was shown is stored in the same update, so the
 *   document keeps reading what was signed;
 * - already signed: stored values only, blanks included (V67).
 */
import { parseCustomerAddress } from './customerAddress'
import { RoofClaimCustomerLike, roofClaimLocked } from './roofClaimGate'

const text = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

/** The phone inputs can hold just a "+1" prefix: not a phone. */
const phone = (value: unknown): string =>
  text(value).replace(/\D/g, '').length >= 7 ? text(value) : ''

export const roofClaimAutofillValues = (
  customer: RoofClaimCustomerLike,
): Readonly<Record<string, string>> => {
  const { city, street, zip } = parseCustomerAddress(
    text(customer['customerAddress']),
  )
  return {
    roof_claiming_homeowner: text(customer['customerName']),
    roof_claiming_phone: phone(customer['customerPhone']),
    roof_claiming_phone_alt: phone(customer['customerPhoneAlt']),
    roof_claiming_email: text(customer['customerEmail']),
    roof_claiming_address: street,
    roof_claiming_city: city,
    roof_claiming_zip: zip,
  }
}

/** Only for the first roof signature; only boxes still empty and not emptied. */
export const roofClaimAutofillPatch = (
  customer: RoofClaimCustomerLike,
  cleared: ReadonlySet<string> = new Set(),
): Record<string, string> => {
  if (roofClaimLocked(customer)) return {}
  return Object.fromEntries(
    Object.entries(roofClaimAutofillValues(customer)).filter(
      ([field, value]) => value && !customer[field] && !cleared.has(field),
    ),
  )
}
